import React, { useEffect, useState, useContext } from 'react';
import {
  Container,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import config from '../config';
import Unauthorized from './Unauthorized';

interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
  is_disabled?: number | boolean;
  disabled_at?: string | null;
  disabled_reason?: string | null;
}

interface Permission {
  id: number;
  name: string;
  description: string;
}

interface AuditLogRow {
  id: number;
  actor_user_id: number;
  target_user_id: number | null;
  action: string;
  details: unknown;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [open, setOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    permissionIds: [] as number[],
  });
  const { permissions: userPermissions } = useContext(AuthContext);

  const canManageUsers = userPermissions.includes('manage_users');

  const [manageOpen, setManageOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [managePermissionIds, setManagePermissionIds] = useState<number[]>([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);
  const [disableReason, setDisableReason] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/permissions`);
      setPermissions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCreateError(null);
    setNewUser({ username: '', email: '', password: '', permissionIds: [] });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const permissionId = parseInt(e.target.value);
    if (e.target.checked) {
      setNewUser({
        ...newUser,
        permissionIds: [...newUser.permissionIds, permissionId],
      });
    } else {
      setNewUser({
        ...newUser,
        permissionIds: newUser.permissionIds.filter(
          (id) => id !== permissionId,
        ),
      });
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${config.API_URL}/users`, newUser);
      fetchUsers();
      handleClose();
    } catch (error) {
      console.error(error);
      setCreateError(
        'Kunde inte skapa användaren. Kontrollera fälten och försök igen.',
      );
    }
  };

  const openManage = async (user: User) => {
    setSelectedUser(user);
    setManageOpen(true);
    setManageError(null);
    setDisableReason(user.disabled_reason ?? '');
    setTab(0);
    setManageLoading(true);
    try {
      const resp = await axios.get(
        `${config.API_URL}/users/${user.id}/permissions`,
      );
      setManagePermissionIds(resp.data?.permissionIds ?? []);
    } catch (e) {
      console.error(e);
      setManageError('Kunde inte hämta användarens behörigheter.');
      setManagePermissionIds([]);
    } finally {
      setManageLoading(false);
    }
  };

  const closeManage = () => {
    setManageOpen(false);
    setSelectedUser(null);
    setManagePermissionIds([]);
    setManageError(null);
    setDisableReason('');
    setAuditLog([]);
    setAuditLoading(false);
  };

  const toggleManagePermission = (permissionId: number, checked: boolean) => {
    if (checked) {
      setManagePermissionIds((prev) => [...prev, permissionId]);
    } else {
      setManagePermissionIds((prev) =>
        prev.filter((id) => id !== permissionId),
      );
    }
  };

  const savePermissions = async () => {
    if (!selectedUser) return;
    setManageLoading(true);
    setManageError(null);
    try {
      await axios.put(
        `${config.API_URL}/users/${selectedUser.id}/permissions`,
        {
          permissionIds: managePermissionIds,
        },
      );
      await fetchUsers();
    } catch (e) {
      console.error(e);
      setManageError('Kunde inte spara behörigheter.');
    } finally {
      setManageLoading(false);
    }
  };

  const disableSelectedUser = async () => {
    if (!selectedUser) return;
    setManageLoading(true);
    setManageError(null);
    try {
      await axios.patch(`${config.API_URL}/users/${selectedUser.id}/disable`, {
        reason: disableReason || null,
      });
      await fetchUsers();
      setSelectedUser({
        ...selectedUser,
        is_disabled: 1,
        disabled_reason: disableReason || null,
      });
    } catch (e) {
      console.error(e);
      setManageError('Kunde inte inaktivera användaren.');
    } finally {
      setManageLoading(false);
    }
  };

  const enableSelectedUser = async () => {
    if (!selectedUser) return;
    setManageLoading(true);
    setManageError(null);
    try {
      await axios.patch(`${config.API_URL}/users/${selectedUser.id}/enable`);
      await fetchUsers();
      setSelectedUser({
        ...selectedUser,
        is_disabled: 0,
        disabled_reason: null,
      });
      setDisableReason('');
    } catch (e) {
      console.error(e);
      setManageError('Kunde inte aktivera användaren.');
    } finally {
      setManageLoading(false);
    }
  };

  const openPasswordDialog = () => {
    setPasswordOpen(true);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const closePasswordDialog = () => {
    setPasswordOpen(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const setPasswordForSelectedUser = async () => {
    if (!selectedUser) return;
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('Lösenordet måste vara minst 8 tecken.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Lösenorden matchar inte.');
      return;
    }

    setManageLoading(true);
    setPasswordError(null);
    try {
      await axios.put(`${config.API_URL}/users/${selectedUser.id}/password`, {
        newPassword,
      });
      closePasswordDialog();
    } catch (e) {
      console.error(e);
      setPasswordError('Kunde inte uppdatera lösenordet.');
    } finally {
      setManageLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    if (!selectedUser) return;
    setAuditLoading(true);
    try {
      const resp = await axios.get(`${config.API_URL}/audit-log`, {
        params: { targetUserId: selectedUser.id, limit: 100, offset: 0 },
      });
      setAuditLog(resp.data ?? []);
    } catch (e) {
      console.error(e);
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setTab(value);
    if (value === 2) {
      fetchAuditLog();
    }
  };

  const deleteUser = async (user: User) => {
    try {
      await axios.delete(`${config.API_URL}/users/${user.id}`);
      fetchUsers();
    } catch (e) {
      console.error(e);
    }
  };

  if (!canManageUsers) {
    return <Unauthorized />;
  }

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <div>
          <Typography variant="h4" gutterBottom>
            Användarhantering
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hantera behörigheter, lösenord och kontostatus. Alla ändringar
            loggas.
          </Typography>
        </div>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Skapa ny användare
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Användarnamn</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Skapad</TableCell>
              <TableCell align="right">Åtgärder</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ py: 2 }}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2">Laddar användare…</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography
                    variant="body2"
                    sx={{ py: 2 }}
                    color="text.secondary"
                  >
                    Inga användare hittades.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const disabled = Boolean(user.is_disabled);
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {disabled ? (
                        <Chip label="Inaktiv" color="warning" size="small" />
                      ) : (
                        <Chip label="Aktiv" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openManage(user)}
                        >
                          Hantera
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => deleteUser(user)}
                        >
                          Ta bort
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog för att skapa ny användare */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Skapa Ny Användare</DialogTitle>
        <DialogContent>
          {createError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Användarnamn"
            name="username"
            fullWidth
            variant="standard"
            value={newUser.username}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Email"
            name="email"
            fullWidth
            variant="standard"
            value={newUser.email}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Lösenord"
            type="password"
            name="password"
            fullWidth
            variant="standard"
            value={newUser.password}
            onChange={handleChange}
          />
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Tilldela Behörigheter
          </Typography>
          <FormGroup>
            {permissions.map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    value={permission.id}
                    checked={newUser.permissionIds.includes(permission.id)}
                    onChange={handlePermissionChange}
                  />
                }
                label={permission.description || permission.name}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Avbryt</Button>
          <Button onClick={handleCreate}>Skapa</Button>
        </DialogActions>
      </Dialog>

      {/* Hantera användare */}
      <Dialog open={manageOpen} onClose={closeManage} maxWidth="md" fullWidth>
        <DialogTitle>
          Hantera användare{selectedUser ? `: ${selectedUser.username}` : ''}
        </DialogTitle>
        <DialogContent>
          {manageError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {manageError}
            </Alert>
          ) : null}

          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Behörigheter" />
            <Tab label="Konto" />
            <Tab label="Auditlogg" />
          </Tabs>

          {tab === 0 ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Tilldela behörigheter
              </Typography>
              {manageLoading ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 2 }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2">Laddar…</Typography>
                </Stack>
              ) : (
                <FormGroup>
                  {permissions.map((permission) => (
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={managePermissionIds.includes(permission.id)}
                          onChange={(e) =>
                            toggleManagePermission(
                              permission.id,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={permission.description || permission.name}
                    />
                  ))}
                </FormGroup>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={closeManage}>Stäng</Button>
                <Button
                  variant="contained"
                  onClick={savePermissions}
                  disabled={manageLoading || !selectedUser}
                >
                  Spara behörigheter
                </Button>
              </Stack>
            </>
          ) : null}

          {tab === 1 ? (
            <>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">Status</Typography>
                  {selectedUser?.is_disabled ? (
                    <Chip label="Inaktiv" color="warning" size="small" />
                  ) : (
                    <Chip label="Aktiv" color="success" size="small" />
                  )}
                </Stack>

                {selectedUser?.is_disabled ? (
                  <Alert severity="warning">
                    Kontot är inaktiverat. Användaren kan inte logga in och
                    tokens blockeras.
                  </Alert>
                ) : null}

                <TextField
                  label="Anledning (valfritt)"
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  fullWidth
                  disabled={manageLoading || !selectedUser}
                />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Button
                    variant="outlined"
                    onClick={openPasswordDialog}
                    disabled={!selectedUser || manageLoading}
                  >
                    Sätt nytt lösenord
                  </Button>

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {selectedUser?.is_disabled ? (
                      <Button
                        variant="contained"
                        onClick={enableSelectedUser}
                        disabled={manageLoading || !selectedUser}
                      >
                        Aktivera konto
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={disableSelectedUser}
                        disabled={manageLoading || !selectedUser}
                      >
                        Inaktivera konto
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={closeManage}>Stäng</Button>
              </Stack>
            </>
          ) : null}

          {tab === 2 ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Auditlogg
              </Typography>
              {auditLoading ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 2 }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2">Laddar logg…</Typography>
                </Stack>
              ) : auditLog.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Ingen logg hittades för användaren.
                </Typography>
              ) : (
                <List dense>
                  {auditLog.map((row) => (
                    <ListItem key={row.id} divider>
                      <ListItemText
                        primary={`${row.action} • admin ${row.actor_user_id}`}
                        secondary={`${new Date(row.created_at).toLocaleString()}${
                          row.details ? ` • ${JSON.stringify(row.details)}` : ''
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={closeManage}>Stäng</Button>
              </Stack>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Sätt nytt lösenord */}
      <Dialog
        open={passwordOpen}
        onClose={closePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sätt nytt lösenord</DialogTitle>
        <DialogContent>
          {passwordError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          ) : null}
          <TextField
            margin="dense"
            label="Nytt lösenord"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Bekräfta lösenord"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={setPasswordForSelectedUser}
            disabled={manageLoading || !selectedUser}
          >
            Spara
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
